<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import { ref, onMounted } from 'vue';
import {
    Bars4Icon, BanknotesIcon, RocketLaunchIcon, ArrowRightOnRectangleIcon,
    BoltIcon,
    ShieldExclamationIcon,
    ChartBarSquareIcon,
    LightBulbIcon,
} from '@heroicons/vue/24/outline';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
});


const activeTab = ref(1);
const tabs = ref(3);
const accordionItems = ref(['Empowering Clients/Insured', 'Empowering Agencies', 'Empowering Certificate Holders'

]);
const showMenu = ref(true);

const toggleNavbar = () => {
    showMenu.value = !showMenu.value;
}

const expandedIndex = ref(null);

const setActiveTab = (index) => {
    activeTab.value = index;
};
const toggleAccordion = (index) => {
    expandedIndex.value = expandedIndex.value === index ? null : index;
};

const currentYear = ref(new Date().getFullYear()); // Reactive property for the current year

const navclasses = 'lg:text-white lg:hover:text-gray-300 text-gray-800 px-3 py-4 lg:py-2 flex items-center text-xs uppercase font-bold';

const form = useForm({
    name: "",
    email: "",
    message: "",
});

const errors = ref({});
const loading = ref(false);

const handleSubmit = () => {
    loading.value = true;
    axios.post(route('enquiry-form'), form)
        .then(() => {
            form.reset();
            Swal.fire({
                toast: true,
                position: "top-end",
                timer: 1500,
                showConfirmButton: false,
                title: 'Success!',
                text: 'Your inquiry has been submitted successfully.',
                icon: 'success',

            });
        })
        .catch((error) => {
            console.error("Error submitting form:", error);
            Swal.fire({
                toast: true,
                title: 'Error!',
                text: 'There was an error submitting your inquiry. Please try again.',
                icon: error,
                position: "top-end",
                timer: 1500,
                showConfirmButton: false,
            });
        })
        .finally(() => {
            loading.value = false; // Reset loading state after request completes
        });
}

</script>
<style>
.tab-button {
    white-space: normal;
    /* Allows text to wrap to a new line */
    word-break: break-word;
    /* Ensures words break if needed */
    text-align: left;
    /* Aligns text to the left for readability */
}

.loader {
    border: 2px solid #f3f3f3;
    /* Light gray */
    border-top: 2px solid #3498db;
    /* Blue */
    border-radius: 50%;
    width: 12px;
    height: 12px;
    animation: spin 0.8s linear infinite;
    display: inline-block;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
<template>

    <Head title="Welcome" />

    <nav class="top-0 absolute z-50 w-full flex flex-wrap items-center justify-between px-2 py-3">
        <div class="container px-4 mx-auto flex flex-wrap items-center justify-between">
            <div class="w-full relative flex justify-between lg:w-auto lg:static lg:block lg:justify-start">
                <a class="text-sm font-bold leading-relaxed inline-block mr-4 py-2 whitespace-nowrap uppercase text-white ml-4"
                    href="#">
                    <!-- <ApplicationLogo class="h-20 w-20" /> -->
                </a>
                <button
                    class="cursor-pointer text-xl leading-none px-3 py-1 border border-solid border-transparent rounded bg-transparent block lg:hidden outline-none focus:outline-none"
                    type="button" @click="toggleNavbar()">
                    <Bars4Icon class="w-7 h-7 text-white" />
                </button>
            </div>
            <div class="lg:flex flex-grow items-center bg-white lg:bg-transparent lg:shadow-none mr-6"
                :class="showMenu ? 'hidden' : ''">
                <ul class="flex flex-col lg:flex-row list-none lg:ml-auto">
                    <li class="flex items-center">
                        <a :class="navclasses" href="#services">
                            <span class="inline-block ml-2">Services</span>
                        </a>
                    </li>
                    <li class="flex items-center">
                        <a :class="navclasses" href="#pricing">
                            <span class="inline-block ml-2">Pricing</span>
                        </a>
                    </li>
                    <li class="flex items-center">
                        <a :class="navclasses" href="#feedback">
                            <span class="inline-block ml-2">Feedback</span>
                        </a>
                    </li>
                    <li>
                        <div class="flex">
                            <Link v-if="$page.props.auth.user" :href="route('dashboard')"
                                class="bg-white flex items-center text-gray-800 active:bg-gray-100 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none lg:mr-1 lg:mb-0 ml-3 mb-3">
                            Dashboard</Link>
                            <template v-else>
                                <Link :href="route('login')"
                                    class="bg-white flex items-center text-gray-800 active:bg-gray-100 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none lg:mr-1 lg:mb-0 ml-3 mb-3">
                                <ArrowRightOnRectangleIcon class="w-5 h-5 mr-2" /> <span>Login</span>
                                </Link>
                                <Link :href="route('register')"
                                    class="bg-white flex items-center text-gray-800 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none lg:mb-0 ml-3 mb-3">
                                Register
                                </Link>

                            </template>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </nav>
    <main>
        <div class="relative pt-16 pb-32 flex content-center items-center justify-center" style="min-height: 60vh;">
            <div class="absolute top-0 w-full h-full bg-center bg-cover"
                style='background-image: url("https://www.theforage.com/blog/wp-content/uploads/2022/11/life-insurance-good-career-path-1024x621.jpg");'>
                <span id="blackOverlay" class="w-full h-full absolute opacity-75 bg-black"></span>
            </div>
            <div class="container relative mx-auto">
                <div class="items-center flex flex-wrap">
                    <div class="w-full lg:w-6/12 px-4 ml-auto mr-auto">
                        <h1
                            class="text-white font-semibold text-4xl mt-10 text-center flex justify-center items-center">
                            Welcome to

                        </h1>
                        <div class="items-center flex justify-center mt-3">
                            <ApplicationLogo class="h-28 w-28 ml-2 " />
                        </div>

                        <p class="mt-3  text-2xl text-gray-300 text-center">
                            Utilizing technology and AI to revolutionize the process of Certificates of Insurance
                            for
                            everyone.
                        </p>
                    </div>
                </div>
            </div>
            <div class="top-auto bottom-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden"
                style="height: 70px;">
                <svg class="absolute bottom-0 overflow-hidden" xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none" version="1.1" viewBox="0 0 2560 100" x="0" y="0">
                    <polygon class="text-shade4 fill-current" points="2560 0 2560 100 0 100"></polygon>
                </svg>
            </div>
        </div>

        <section class="bg-gray-200">

            <!-- <div class="top-auto bottom-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20"
                style="height: 70px;">
                <svg class="absolute bottom-0 overflow-hidden" xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none" version="1.1" viewBox="0 0 2560 100" x="0" y="0">
                    <polygon class="text-shade4 fill-current" points="2560 0 2560 100 0 100"></polygon>
                </svg>
            </div> -->

            <div class="container mx-auto md:p-12  sm:block p-1 ">

                <div id="accordion-collapse" class="sm:hidden pt-5 pb-5">
                    <div v-for=" (item, index) in accordionItems" :key="index">
                        <h2>
                            <button type="button"
                                class="flex items-center justify-between w-full p-3 font-medium rtl:text-right bg-white border border-b-0 border-gray-200 focus:ring-4 focus:ring-gray-200 hover:bg-gray-50 gap-2"
                                @click="toggleAccordion(index)" :aria-expanded="expandedIndex === index"
                                :aria-controls="'accordion-collapse-body-' + (index + 1)" :class="{
                                    'rounded-t-xl': index === 0,
                                    ' text-blue-600 border-b-blue-600 bg-blue-50': expandedIndex === index,
                                    'text-gray-500': expandedIndex !== index,
                                }">
                                <div class="sm:flex md:gap-2 text-start">
                                    <h3 class="text-xl font-semibold">{{ item }}</h3>
                                    <span class="md:mt-1.5 text-blue-600 font-semibold underline">
                                        Click Here To Learn More</span>
                                </div>

                                <svg data-accordion-icon :class="{ 'rotate-180': expandedIndex === index }"
                                    class="w-3 h-3 shrink-0 transition-transform" xmlns="http://www.w3.org/2000/svg"
                                    fill="none" viewBox="0 0 10 6">
                                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                        stroke-width="2" d="M9 5 5 1 1 5" />
                                </svg>
                            </button>
                        </h2>
                        <div :id="'accordion-collapse-body-' + (index + 1)" v-show="expandedIndex === index"
                            :aria-labelledby="'accordion-collapse-heading-' + (index + 1)" class=" overflow-y-auto
                    border border-gray-200
                      text-lg font-light bg-white">

                            <div v-if="expandedIndex == 0" class="p-3 ">
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Say goodbye to the traditional method of manually requesting/renewing your
                                        Certificate
                                        of
                                        Insurances/COIs with COI Center.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Eliminates the traditional method of manually requesting and waiting for COIs to
                                        be
                                        processed and forwarded. By logging into the COI Center, our
                                        web-based/mobile-friendly software will <span
                                            class="underline font-semibold">instantly</span> create and send COIs
                                        anytime,
                                        anywhere, 24/7/365. No more manual requests or delays.
                                    </span>

                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Do you work with multiple insurance brokers? The COI Center allows you to <span
                                            class="underline font-semibold">instantly</span> request COIs from multiple
                                        brokers
                                        through our single, centralized platform.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">

                                        Simplify your renewal process with our COI Center’s automated systems. At
                                        renewal
                                        time,
                                        you
                                        can <span class="underline font-semibold">instantly</span> choose to auto-renew
                                        all
                                        COIs
                                        or
                                        select specific ones. This eliminates the need for unproductive manual renewals,
                                        saving
                                        you
                                        time and effort.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Include all endorsements/waivers/proofs <span
                                            class="underline font-semibold">instantly</span> with the original COI
                                        request
                                        rather
                                        than
                                        subsequent back and forth emails with certificate holders.
                                    </span>
                                </div>
                                <h1 class="mt-3 font-bold text-lg">
                                    BONUS: Use the COI Center to stay compliant with your Sub-Contractors
                                </h1>

                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Maintain and automate your sub-contractors COIs to ensure you are protected and
                                        remain
                                        protected.
                                    </span>
                                </div>

                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Invite your sub-contractors to upload their COIs where our AI system will help
                                        you
                                        maintain
                                        compliance on a daily basis, so expirations are never missed, therefore you
                                        never
                                        fall
                                        out
                                        of compliance.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        You can set your insurance requirements for your sub-contractors on a specific
                                        job.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Save time, money and frustration during audits. Use the COI Center to
                                        proactively,
                                        securely
                                        store COIs, making them easily accessible when needed. No more scrambling to
                                        find
                                        missing
                                        documents and save you
                                        <span class="underline font-semibold">thousands of
                                            dollars.</span>
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        The COI Center will automatically send you and your sub-contractor a push
                                        notification
                                        when
                                        specific insurance items are set to expire or fall out of compliance.
                                    </span>
                                </div>

                            </div>
                            <div v-if="expandedIndex == 1" class="p-3">
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Optimize your operations by outsourcing COI requests to the COI Center. This
                                        allows
                                        you
                                        to
                                        focus on core business activities while providing exceptional service to your
                                        clients.
                                    </span>
                                </div>

                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Meet your clients/insureds' demands for immediate access to COIs. Our platform
                                        provides
                                        round-the-clock availability, ensuring they can <span
                                            class="underline font-semibold">instantly</span> obtain certificates
                                        whenever
                                        they
                                        need
                                        them,<span class="font-semibold"> 24/7/365.</span>
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Enhance your clients/insureds' risk management capabilities by using the COI
                                        Center
                                        to track and manage subcontractor insurance. This proactive approach helps them
                                        avoid potential claims and unexpected costs during audits.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Reduce your Error & Omissions exposure by using the COI Center; we have you
                                        covered
                                        with our E&O!
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Include all endorsements/waivers/proofs instantly with the original COI request
                                        rather than subsequent back and forth emails with certificate holders.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Streamline your renewal process by automating COI issuance. At renewal time, you
                                        can
                                        instantly select and send all or specific COIs directly from our system,
                                        eliminating
                                        the need for manual exports and individual emails each year.
                                    </span>
                                </div>
                            </div>
                            <div v-if="expandedIndex == 2" class="p-3">
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        COI Center is transforming the Certificate of Insurance industry by leveraging
                                        technology to
                                        mitigate risk and streamline management. Our innovative AI solution empowers you
                                        to
                                        automate
                                        the entire process, ensuring efficiency and compliance.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        The COI Center informs you <span
                                            class="underline font-semibold">instantly</span> if
                                        a
                                        policy is in force, cancelled, updated and/or renewed rather than the
                                        traditional
                                        snail
                                        mail
                                        method which you may not receive at all.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        COI Center helps safeguard your organization by ensuring proper insurance
                                        coverage.
                                        The
                                        COI
                                        Center facilitates the collection and verification of COIs from your partners
                                        and
                                        subcontractors to reduce risk while saving you time and money.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Invite your certificate holders to upload their COIs to our platform. Our
                                        AI-powered
                                        platform will automatically monitor expiration dates, ensuring your organization
                                        remains
                                        compliant at all times.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Take control of your risk management by selecting your specific insurance
                                        requirements
                                        for
                                        each project or subcontractor.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        The AI powered COI Center will automatically send you and your certificate
                                        holder
                                        push
                                        notifications when any specific insurance item is set to expire or fall out of
                                        compliance.
                                    </span>
                                </div>
                                <div class="flex">
                                    <li class="mt-2"> </li>
                                    <span class="mt-1.5">
                                        Protect your organization by maintaining a secure, online and centralized
                                        repository
                                        of
                                        COIs. In the event of a claim, this ensures a proactive approach to access
                                        critical
                                        documents, saving you time, money and reducing risk.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="relative z-0 md:flex  border-gray-200 border mb-10  rounded-lg bg-white overflow-hidden sm:block hidden"
                    aria-label="Tabs" role="tablist" aria-orientation="horizontal">

                    <button v-for="index in tabs" :key="index" type="button"
                        class=" tab-button hs-tab-active:border-b-blue-600 relative dark:hs-tab-active:border-b-blue-600 min-w-0 flex-1 first:border-s-0 border-s border-b-2 py-4 px-4 text-sm font-medium text-center overflow-hidden hover:bg-gray-50 focus:z-10 focus:outline-none focus:text-blue-600"
                        :class="{
                            ' text-blue-600 border-b-blue-600 bg-blue-50': activeTab === index,
                            'text-gray-500': activeTab !== index,
                        }" :aria-selected="activeTab === index" @click="setActiveTab(index)" :id="'tab-item-' + index"
                        :aria-controls="'tab-content-' + index" role="tab">
                        <div v-if="index === 1">
                            <h3 class="text-xl font-semibold text-center"> Empowering Clients/Insured</h3>
                            <div class="text-blue-600 text-sm   font-semibold text-center underline">Click Here To Learn
                                More</div>
                        </div>
                        <div v-if="index === 2">
                            <h3 class="text-xl font-semibold text-center">Empowering Agencies</h3>
                            <div class="text-blue-600 text-sm   font-semibold text-center underline">Click Here To Learn
                                More</div>
                        </div>
                        <div v-if="index === 3">
                            <h3 class="text-xl font-semibold text-center" v-if="index === 3">Empowering Certificate
                                Holders
                            </h3>
                            <div class="text-blue-600 text-sm   font-semibold text-center underline">Click Here To Learn
                                More</div>
                        </div>
                    </button>
                </div>

                <!-- Tabs Content -->
                <div v-for="index in tabs" :key="index" :id="'tab-content-' + index" class="
                    overflow-y-auto
                    border border-gray-200
                    rounded-lg text-lg font-light leading-relaxed sm:block hidden
                    " :aria-labelledby="'tab-item-' + index" role="tabpanel" :aria-hidden="activeTab !== index"
                    v-show="activeTab === index">
                    <div v-if="index == 1" class="box p-4 ">
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Say goodbye to the traditional method of manually requesting/renewing your Certificate
                                of
                                Insurances/COIs with COI Center.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Eliminates the traditional method of manually requesting and waiting for COIs to be
                                processed and forwarded. By logging into the COI Center, our web-based/mobile-friendly
                                software will <span class="underline font-semibold">instantly</span> create and send
                                COIs
                                anytime, anywhere, 24/7/365. No more manual requests or delays.
                            </span>

                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Do you work with multiple insurance brokers? The COI Center allows you to <span
                                    class="underline font-semibold">instantly</span> request COIs from multiple brokers
                                through our single, centralized platform.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">

                                Simplify your renewal process with our COI Center’s automated systems. At renewal time,
                                you
                                can <span class="underline font-semibold">instantly</span> choose to auto-renew all COIs
                                or
                                select specific ones. This eliminates the need for unproductive manual renewals, saving
                                you
                                time and effort.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Include all endorsements/waivers/proofs <span
                                    class="underline font-semibold">instantly</span> with the original COI request
                                rather
                                than
                                subsequent back and forth emails with certificate holders.
                            </span>
                        </div>
                        <h1 class="mt-3 font-bold text-lg">
                            BONUS: Use the COI Center to stay compliant with your Sub-Contractors
                        </h1>

                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Maintain and automate your sub-contractors COIs to ensure you are protected and remain
                                protected.
                            </span>
                        </div>

                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Invite your sub-contractors to upload their COIs where our AI system will help you
                                maintain
                                compliance on a daily basis, so expirations are never missed, therefore you never fall
                                out
                                of compliance.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                You can set your insurance requirements for your sub-contractors on a specific job.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Save time, money and frustration during audits. Use the COI Center to proactively,
                                securely
                                store COIs, making them easily accessible when needed. No more scrambling to find
                                missing
                                documents and save you
                                <span class="underline font-semibold">thousands of
                                    dollars.</span>
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                The COI Center will automatically send you and your sub-contractor a push notification
                                when
                                specific insurance items are set to expire or fall out of compliance.
                            </span>
                        </div>

                    </div>
                    <div v-if="index == 2" class="box p-4">
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Optimize your operations by outsourcing COI requests to the COI Center. This allows you
                                to
                                focus on core business activities while providing exceptional service to your clients.
                            </span>
                        </div>

                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Meet your clients/insureds' demands for immediate access to COIs. Our platform provides
                                round-the-clock availability, ensuring they can <span
                                    class="underline font-semibold">instantly</span> obtain certificates whenever they
                                need
                                them,<span class="font-semibold"> 24/7/365.</span>
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Enhance your clients/insureds' risk management capabilities by using the COI Center to
                                track
                                and manage subcontractor insurance. This proactive approach helps them avoid potential
                                claims and unexpected costs during audits.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Reduce your Error & Omissions exposure by using the COI Center; we have you covered with
                                our
                                E&O!
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Include all endorsements/waivers/proofs instantly with the original COI request rather
                                than
                                subsequent back and forth emails with certificate holders.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Streamline your renewal process by automating COI issuance. At renewal time, you can
                                instantly select and send all or specific COIs directly from our system, eliminating the
                                need for manual exports and individual emails each year.
                            </span>
                        </div>
                    </div>
                    <div v-if="index == 3" class="box p-4">
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                COI Center is transforming the Certificate of Insurance industry by leveraging
                                technology to
                                mitigate risk and streamline management. Our innovative AI solution empowers you to
                                automate
                                the entire process, ensuring efficiency and compliance.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                The COI Center informs you <span class="underline font-semibold">instantly</span> if a
                                policy is in force, cancelled, updated and/or renewed rather than the traditional snail
                                mail
                                method which you may not receive at all.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                COI Center helps safeguard your organization by ensuring proper insurance coverage. The
                                COI
                                Center facilitates the collection and verification of COIs from your partners and
                                subcontractors to reduce risk while saving you time and money.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Invite your certificate holders to upload their COIs to our platform. Our AI-powered
                                platform will automatically monitor expiration dates, ensuring your organization remains
                                compliant at all times.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Take control of your risk management by selecting your specific insurance requirements
                                for
                                each project or subcontractor.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                The AI powered COI Center will automatically send you and your certificate holder push
                                notifications when any specific insurance item is set to expire or fall out of
                                compliance.
                            </span>
                        </div>
                        <div class="flex">
                            <li class="mt-2"> </li>
                            <span class="mt-1.5">
                                Protect your organization by maintaining a secure, online and centralized repository of
                                COIs. In the event of a claim, this ensures a proactive approach to access critical
                                documents, saving you time, money and reducing risk.
                            </span>
                        </div>
                    </div>

                </div>

            </div>

        </section>

        <section class="bg-shade1 py-10">
            <div class="bottom-auto top-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20"
                style="height: 70px;">
                <svg class="absolute bottom-0 overflow-hidden" xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none" version="1.1" viewBox="0 0 2560 100" x="0" y="0">
                    <polygon class="text-shade4 fill-current" points="2560 0 2560 100 0 100"></polygon>
                </svg>
            </div>
            <div class="container mx-auto">
                <div class="md:flex items-center mx-auto ">
                    <div class="w-full md:w-7/12 px-4 mr-auto ml-auto">

                        <h3 class="text-3xl mb-2 font-semibold leading-normal text-white">
                            Pioneering the Management of Certificate of Insurances for You
                        </h3>
                        <p class="text-lg font-light leading-relaxed mt-4 mb-4 text-white text-justify">
                            At COI Center, we're not just another company. We're a dedicated team on a mission to
                            revolutionize the way Certificate of Insurance (COI) are managed. Our passion for
                            simplifying
                            the process while protecting all drives us every day. You can count on us
                            to be your trusted partner in making the management of Certificate of Insurance stress-free
                        </p>
                        <p class="text-lg font-light leading-relaxed mt-0 mb-4 text-white text-justify">
                            With years of experience and a commitment to efficiency, we've created a platform that's as
                            user-friendly as it is effective. We're not just about technology; we're about making your
                            life
                            easie
                        </p>
                        <p class="font-bold text-white mt-6 text-lg text-justify">Your Instant, Automated way to a
                            Streamline, Simple, and Stress-Free Certificate of Insurance.</p>
                    </div>
                    <div class="w-full md:w-4/12  px-4 mr-auto ml-auto">
                        <div
                            class=" relative flex flex-col min-w-0 break-words sm:block mt-5 w-full mb-6 shadow-lg rounded-lg bg-pink-600">
                            <img alt="..."
                                src="https://img1.wsimg.com/isteam/ip/9a5e336f-1a18-4ad0-96a4-b6e0bd674146/Hands.jpg"
                                class="w-full align-middle rounded-t-lg h-70" />
                            <blockquote class="relative p-8 mb-4 hidden">
                                <svg preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 583 95"
                                    class="absolute left-0 w-full block" style="height: 95px; top: -94px;">
                                    <polygon points="-30,95 583,95 583,65" class="text-shade4 fill-current"></polygon>
                                </svg>
                                <h4 class="text-xl font-bold text-white">
                                    Top Notch Services
                                </h4>
                                <p class="text-md font-light mt-2 text-white">
                                    The Arctic Ocean freezes every winter and much of the
                                    sea-ice then thaws every summer, and that process will
                                    continue whatever happens.
                                </p>
                            </blockquote>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section id="services" class="relative py-20 bg-white">
            <div class="bottom-auto top-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20"
                style="height: 80px;">
                <svg class="absolute bottom-0 overflow-hidden" xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none" version="1.1" viewBox="0 0 2560 100" x="0" y="0">
                    <polygon class="text-shade4 fill-current" points="2560 0 2560 100 0 100"></polygon>
                </svg>
            </div>
            <div class="container mx-auto px-4 ">
                <div class="items-center flex flex-wrap ">
                    <div class="w-full md:w-4/12 ml-auto mr-auto px-4">
                        <img alt="..." class="max-w-full rounded-lg shadow-lg h-80"
                            src="https://s31898.pcdn.co/wp-content/uploads/2022/11/short-term-insurance-e1668670096528-800x430.jpg" />
                    </div>
                    <div class="w-full md:w-5/12 ml-auto mr-auto px-4 mt-5 text-left">
                        <div>
                            <div
                                class="text-pink-600 p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-6 shadow-lg rounded-full bg-pink-300">
                                <RocketLaunchIcon class="w-6 h-6" />
                            </div>
                            <h3 class="text-3xl font-semibold">Our Comprehensive COI Services</h3>
                            <p class="mt-4 text-lg leading-relaxed text-gray-600">
                                At COI Center, we offer a suite of services.
                            </p>
                            <ul class="list-none mt-6 text-left">
                                <li class="py-2 ">
                                    <div class="flex ">
                                        <div class=" flex">
                                            <span
                                                class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                                <BoltIcon class="w-3 h-3" />
                                            </span>
                                            <h4 class="text-gray-600">
                                                Effortless COI Generation
                                            </h4>
                                        </div>
                                    </div>
                                </li>
                                <li class="py-2">
                                    <div class="flex">
                                        <div class=" flex">
                                            <span
                                                class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                                <BoltIcon class="w-3 h-3" />
                                            </span>
                                            <h4 class="text-gray-600">
                                                Efficient Agency Collaboration
                                            </h4>
                                        </div>
                                    </div>

                                </li>
                                <li class="py-2 ">
                                    <div class="flex">
                                        <div class="flex">
                                            <span
                                                class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                                <BoltIcon class="w-3 h-3" />
                                            </span>
                                            <h4 class="text-gray-600">
                                                Client-Centric Approach
                                            </h4>
                                        </div>
                                    </div>
                                </li>
                                <li class="py-2 ">
                                    <div class="flex">
                                        <div class=" flex">
                                            <span
                                                class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                                <BoltIcon class="w-3 h-3" />
                                            </span>
                                            <h4 class="text-gray-600">
                                                User-Friendly Interface
                                            </h4>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section id="testimonials" class="pt-20 pb-48 bg-white hidden">
            <div class="container mx-auto px-4">
                <div class="flex flex-wrap justify-center text-center mb-24">
                    <div class="w-full lg:w-6/12 px-4">
                        <h2 class="text-4xl font-semibold">Here are our heroes</h2>
                        <p class="text-lg leading-relaxed m-4 text-gray-600">
                            According to the National Oceanic and Atmospheric
                            Administration, Ted, Scambos, NSIDClead scentist, puts the
                            potentially record maximum.
                        </p>
                    </div>
                </div>
                <div class="flex flex-wrap">
                    <div class="w-full md:w-6/12 lg:w-3/12 lg:mb-0 mb-12 px-4">
                        <div class="px-6">
                            <img alt="..." src="/landingPage/img/team-1-800x800.jpg"
                                class="shadow-lg rounded-full max-w-full mx-auto" style="max-width: 120px;" />
                            <div class="pt-6 text-center">
                                <h5 class="text-xl font-bold">Ryan Tompson</h5>
                                <p class="mt-1 text-sm text-gray-500 uppercase font-semibold">
                                    Web Developer
                                </p>
                                <div class="mt-6">
                                    <button
                                        class="bg-blue-400 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-twitter"></i></button><button
                                        class="bg-blue-600 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-facebook-f"></i></button><button
                                        class="bg-pink-500 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-dribbble"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="w-full md:w-6/12 lg:w-3/12 lg:mb-0 mb-12 px-4">
                        <div class="px-6">
                            <img alt="..." src="/landingPage/img/team-2-800x800.jpg"
                                class="shadow-lg rounded-full max-w-full mx-auto" style="max-width: 120px;" />
                            <div class="pt-6 text-center">
                                <h5 class="text-xl font-bold">Romina Hadid</h5>
                                <p class="mt-1 text-sm text-gray-500 uppercase font-semibold">
                                    Marketing Specialist
                                </p>
                                <div class="mt-6">
                                    <button
                                        class="bg-red-600 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-google"></i>
                                    </button>
                                    <button
                                        class="bg-blue-600 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-facebook-f"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="w-full md:w-6/12 lg:w-3/12 lg:mb-0 mb-12 px-4">
                        <div class="px-6">
                            <img alt="..." src="/landingPage/img/team-3-800x800.jpg"
                                class="shadow-lg rounded-full max-w-full mx-auto" style="max-width: 120px;" />
                            <div class="pt-6 text-center">
                                <h5 class="text-xl font-bold">Alexa Smith</h5>
                                <p class="mt-1 text-sm text-gray-500 uppercase font-semibold">
                                    UI/UX Designer
                                </p>
                                <div class="mt-6">
                                    <button
                                        class="bg-red-600 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-google"></i></button><button
                                        class="bg-blue-400 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-twitter"></i></button><button
                                        class="bg-gray-800 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-instagram"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="w-full md:w-6/12 lg:w-3/12 lg:mb-0 mb-12 px-4">
                        <div class="px-6">
                            <img alt="..." src="/landingPage/img/team-4-470x470.png"
                                class="shadow-lg rounded-full max-w-full mx-auto" style="max-width: 120px;" />
                            <div class="pt-6 text-center">
                                <h5 class="text-xl font-bold">Jenna Kardi</h5>
                                <p class="mt-1 text-sm text-gray-500 uppercase font-semibold">
                                    Founder and CEO
                                </p>
                                <div class="mt-6">
                                    <button
                                        class="bg-pink-500 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-dribbble"></i></button><button
                                        class="bg-red-600 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-google"></i></button><button
                                        class="bg-blue-400 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-twitter"></i></button><button
                                        class="bg-gray-800 text-white w-8 h-8 rounded-full outline-none focus:outline-none mr-1 mb-1"
                                        type="button">
                                        <i class="fab fa-instagram"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <hr />
        <section id="pricing" class="pb-20  text-white bg-shade2 ">
            <div class="container mx-auto px-4">
                <div class="flex flex-wrap justify-center text-center mb-24">
                    <div class="w-full lg:w-6/12 px-4">
                        <h2 class="text-4xl font-semibold mt-8">Pricing</h2>
                        <p class="text-lg leading-relaxed m-4 text-white">
                            Choose the Perfect Plan for Your Needs.<br />
                            At COI Center, we offer pricing plans that cater to different requirements, ensuring you
                            find
                            the ideal fit for your COI/Certificate of Insurance management.
                        </p>
                    </div>
                </div>
                <div class="flex flex-wrap justify-center ">
                    <div class="lg:pt-12 pt-6 w-full md:w-4/12 px-4 text-center hidden">
                        <div
                            class="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg">
                            <div class="px-4 py-5 flex-auto">
                                <div
                                    class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-red-400">
                                    <BanknotesIcon class="w-5 h-5 text-white" />
                                </div>
                                <h6 class="text-xl font-semibold">Basic Plan</h6>
                                <p class="mt-2 mb-4 text-gray-600">
                                    Divide details about your product or agency work into parts.
                                    A paragraph describing a feature will be enough.
                                </p>
                                <ul class="list-none mt-6">
                                    <li class="py-2 flex items-center">
                                        <span
                                            class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                            <BoltIcon class="w-3 h-3" />
                                        </span>
                                        <h4 class="text-gray-600">
                                            Carefully crafted components
                                        </h4>
                                    </li>
                                    <li class="py-2 flex items-center">
                                        <span
                                            class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                            <BoltIcon class="w-3 h-3" />
                                        </span>
                                        <h4 class="text-gray-600">
                                            Carefully crafted components
                                        </h4>
                                    </li>
                                    <li class="py-2 flex items-center">
                                        <span
                                            class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                            <BoltIcon class="w-3 h-3" />
                                        </span>
                                        <h4 class="text-gray-600">
                                            Carefully crafted components
                                        </h4>
                                    </li>
                                    <li class="py-2 flex items-center">
                                        <span
                                            class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                            <BoltIcon class="w-3 h-3" />
                                        </span>
                                        <h4 class="text-gray-600">
                                            Carefully crafted components
                                        </h4>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="w-full md:w-4/12 px-4 text-center">
                        <div
                            class="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg">
                            <div class="px-4 py-5 flex-auto">
                                <div
                                    class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-blue-400">
                                    <BanknotesIcon class="w-5 h-5 text-white" />
                                </div>
                                <h6 class="text-xl font-semibold text-black">Direct Client Plan: Simple & Efficient</h6>
                                <p class="mt-2 mb-4 text-gray-600">
                                    The Client Specific Plan provides the essentials for hassle-free COI/Certificate of
                                    Insurance management. Perfect for companies looking to streamline their processes.
                                    Agency Plan: Elevated Efficiency

                                </p>
                                <li class="py-2 flex items-center hidden ">
                                    <span
                                        class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                        <BoltIcon class="w-3 h-3" />
                                    </span>
                                    <h4 class="text-gray-600">
                                        Carefully crafted components
                                    </h4>
                                </li>
                                <li class="py-2 flex items-center hidden">
                                    <span
                                        class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                        <BoltIcon class="w-3 h-3" />
                                    </span>
                                    <h4 class="text-gray-600">
                                        Carefully crafted components
                                    </h4>
                                </li>
                                <li class="py-2 flex items-center hidden">
                                    <span
                                        class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                        <BoltIcon class="w-3 h-3" />
                                    </span>
                                    <h4 class="text-gray-600 ">
                                        Carefully crafted components
                                    </h4>
                                </li>
                            </div>
                        </div>
                    </div>
                    <div class="w-full md:w-4/12 px-4 text-center">
                        <div
                            class="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg">
                            <div class="px-4 py-2.5 flex-auto">
                                <div
                                    class="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-full bg-green-400">
                                    <BanknotesIcon class="w-5 h-5 text-white" />
                                </div>
                                <h6 class="text-xl font-semibold text-black">Agency Plan: Elevated Efficiency</h6>
                                <p class="  text-gray-600 mb-4 mt-2">
                                    Upgrade to the Agency Plan for additional features and convenience to manage all or
                                    multiple clients. Ideal for organizations seeking to
                                    enhanced their agencies and customers COI/Certificate of Insurance’s experience and
                                    capabilities.
                                </p>
                                <li class="py-2 flex items-center hidden">
                                    <span
                                        class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                        <BoltIcon class="w-3 h-3" />
                                    </span>
                                    <h4 class="text-gray-600">
                                        Carefully crafted components
                                    </h4>
                                </li>
                                <li class="py-2 flex items-center hidden">
                                    <span
                                        class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                        <BoltIcon class="w-3 h-3" />
                                    </span>
                                    <h4 class="text-gray-600">
                                        Carefully crafted components
                                    </h4>
                                </li>
                                <li class="py-2 flex items-center hidden">
                                    <span
                                        class="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-pink-600 bg-pink-200 mr-3">
                                        <BoltIcon class="w-3 h-3" />
                                    </span>
                                    <h4 class="text-gray-600">
                                        Carefully crafted components
                                    </h4>
                                </li>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </section>

        <section id="feedback" class="pb-20 relative block bg-shade1">
            <div class="bottom-auto top-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20"
                style="height: 80px;">
                <svg class="absolute bottom-0 overflow-hidden" xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none" version="1.1" viewBox="0 0 2560 100" x="0" y="0">
                    <polygon class="text-shade4 fill-current" points="2560 0 2560 100 0 100"></polygon>
                </svg>
            </div>
            <div class="container mx-auto px-4 lg:pt-24 lg:pb-64">
                <div class="flex flex-wrap text-center justify-center">
                    <div class="w-full lg:w-6/12 px-4">
                        <h2 class="text-4xl font-semibold text-white mt-5">Your Voice Matters</h2>
                        <p class="text-lg leading-relaxed mt-4 mb-4 text-white">
                            Your feedback is invaluable to us. We're here to provide the support you need and to
                            continuously enhance your COI Center experience.
                        </p>
                    </div>
                </div>
                <div class="flex flex-wrap mt-12 justify-center">
                    <div class="w-full lg:w-3/12 px-4 text-center">
                        <div
                            class="text-gray-900 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center">
                            <ShieldExclamationIcon class="w-5 h-5" />
                        </div>
                        <h6 class="text-xl mt-5 font-semibold text-white">
                            Quick Assistance
                        </h6>
                        <p class="mt-2 mb-4 text-white">
                            Our dedicated support team is just a message away.
                            We're here to assist you promptly and efficiently.
                        </p>
                    </div>
                    <div class="w-full lg:w-3/12 px-4 text-center">
                        <div
                            class="text-gray-900 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center">
                            <ChartBarSquareIcon class="w-5 h-5" />
                        </div>
                        <h5 class="text-xl mt-5 font-semibold text-white">
                            Feature Requests
                        </h5>
                        <p class="mt-2 mb-4 text-white">
                            Have a suggestion for a new feature or improvement? We're all ears.
                            Share your ideas, and we'll consider them for future updates.
                        </p>
                    </div>
                    <div class="w-full lg:w-3/12 px-4 text-center">
                        <div
                            class="text-gray-900 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center">
                            <LightBulbIcon class="w-5 h-5" />
                        </div>
                        <h5 class="text-xl mt-5 font-semibold text-white">Help Center</h5>
                        <p class="mt-2 mb-4 text-white">
                            Explore our comprehensive Help Center for quick answers to common questions.
                            It's your go-to resource for COI Center guidance.
                        </p>
                    </div>
                </div>
            </div>
        </section>
        <section class="relative block py-24 lg:pt-0 bg-shade1">
            <div class="container mx-auto px-4">
                <div class="flex flex-wrap justify-center lg:-mt-64 -mt-48">
                    <div class="w-full lg:w-6/12 px-4">
                        <div
                            class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-md rounded-lg bg-gray-100 mt-5">
                            <div class="flex-auto p-5 lg:p-10">
                                <h4 class="text-2xl font-semibold">Want to work with us?</h4>
                                <p class="leading-relaxed mt-1 mb-4">
                                    Complete this form and we will get back to you in 24 hours.
                                </p>
                                <form @submit.prevent="handleSubmit">
                                    <div class="relative w-full mb-3 mt-8">
                                        <label class="block uppercase text-xs font-bold mb-2" for="full-name">Full
                                            Name</label>
                                        <input type="text" id="full-name" v-model="form.name"
                                            class="border-0 px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                                            placeholder="Full Name" style="transition: all 0.15s ease 0s;" />
                                        <p v-if="errors.name" class="text-red-500 text-xs mt-1">{{ errors.name }}</p>
                                    </div>
                                    <div class="relative w-full mb-3">
                                        <label class="block uppercase text-xs font-bold mb-2" for="email">Email</label>
                                        <input type="email" id="email" v-model="form.email"
                                            class="border-0 px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                                            placeholder="Email" style="transition: all 0.15s ease 0s;" />
                                        <p v-if="errors.email" class="text-red-500 text-xs mt-1">{{ errors.email }}</p>
                                    </div>
                                    <div class="relative w-full mb-3">
                                        <label class="block uppercase text-xs font-bold mb-2"
                                            for="message">Message</label>
                                        <textarea id="message" rows="4" v-model="form.message"
                                            class="border-0 px-3 py-3 placeholder-gray-400 text-gray-700 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                                            placeholder="Type a message..."></textarea>
                                        <p v-if="errors.message" class="text-red-500 text-xs mt-1">{{ errors.message }}
                                        </p>
                                    </div>
                                    <div class="text-center mt-6">
                                        <button
                                            class="bg-black text-white active:bg-gray-700 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1"
                                            type="submit" style="transition: all 0.15s ease 0s;">
                                            <template v-if="loading">
                                                <span class="loader mr-2"></span> Sending...
                                            </template>
                                            <template v-else>
                                                Send Message
                                            </template>
                                        </button>

                                    </div>
                                </form>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    </main>
    <footer class="relative bg-white pt-8 pb-6">
        <div class="bottom-auto top-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20"
            style="height: 80px;">
            <svg class="absolute bottom-0 overflow-hidden" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"
                version="1.1" viewBox="0 0 2560 100" x="0" y="0">
                <polygon class="text-shade4 fill-current" points="2560 0 2560 100 0 100"></polygon>
            </svg>
        </div>
        <div class="container mx-auto px-4">
            <div class="flex flex-wrap">
                <div class="w-full lg:w-6/12 px-4">
                    <h4 class="text-3xl font-semibold">Let's keep in touch!</h4>
                    <h5 class="text-lg mt-0 mb-2 text-gray-700">
                        Find us on any of these platforms, we respond 1-2 business days.
                    </h5>
                    <div class="mt-6">
                        <button
                            class="bg-white text-blue-400 shadow-lg font-normal  items-center justify-center align-center rounded-full outline-none focus:outline-none mr-2 p-3"
                            type="button">
                            <span class="[&>svg]:h-5"><svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                                    viewBox="0 0 512 512">
                                    <!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc. -->
                                    <path
                                        d="M459.4 151.7c.3 4.5 .3 9.1 .3 13.6 0 138.7-105.6 298.6-298.6 298.6-59.5 0-114.7-17.2-161.1-47.1 8.4 1 16.6 1.3 25.3 1.3 49.1 0 94.2-16.6 130.3-44.8-46.1-1-84.8-31.2-98.1-72.8 6.5 1 13 1.6 19.8 1.6 9.4 0 18.8-1.3 27.6-3.6-48.1-9.7-84.1-52-84.1-103v-1.3c14 7.8 30.2 12.7 47.4 13.3-28.3-18.8-46.8-51-46.8-87.4 0-19.5 5.2-37.4 14.3-53 51.7 63.7 129.3 105.3 216.4 109.8-1.6-7.8-2.6-15.9-2.6-24 0-57.8 46.8-104.9 104.9-104.9 30.2 0 57.5 12.7 76.7 33.1 23.7-4.5 46.5-13.3 66.6-25.3-7.8 24.4-24.4 44.8-46.1 57.8 21.1-2.3 41.6-8.1 60.4-16.2-14.3 20.8-32.2 39.3-52.6 54.3z" />
                                </svg>
                            </span></button><button
                            class="bg-white text-blue-600 shadow-lg font-normal  items-center justify-center align-center rounded-full outline-none focus:outline-none mr-2 p-3"
                            type="button">
                            <span class="[&>svg]:fill-[#1877f2] [&>svg]:h-5">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                    <path
                                        d="M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z" />
                                </svg>
                            </span>
                        </button>
                        <button
                            class="bg-white text-pink-400 shadow-lg font-normal  items-center justify-center align-center rounded-full outline-none focus:outline-none mr-2 p-3"
                            type="button">
                            <span class="[&>svg]:h-5  [&>svg]:fill-[#ea4c89]">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 512 512">
                                    <!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc. -->
                                    <path
                                        d="M256 8C119.3 8 8 119.3 8 256s111.3 248 248 248 248-111.3 248-248S392.7 8 256 8zm164 114.4c29.5 36 47.4 82 47.8 132-7-1.5-77-15.7-147.5-6.8-5.8-14-11.2-26.4-18.6-41.6 78.3-32 113.8-77.5 118.3-83.5zM396.4 97.9c-3.8 5.4-35.7 48.3-111 76.5-34.7-63.8-73.2-116.2-79-124 67.2-16.2 138 1.3 190.1 47.5zm-230.5-33.3c5.6 7.7 43.4 60.1 78.5 122.5-99.1 26.3-186.4 25.9-195.8 25.8C62.4 147.2 106.7 92.6 165.9 64.6zM44.2 256.3c0-2.2 0-4.3 .1-6.5 9.3 .2 111.9 1.5 217.7-30.1 6.1 11.9 11.9 23.9 17.2 35.9-76.6 21.6-146.2 83.5-180.5 142.3C64.8 360.4 44.2 310.7 44.2 256.3zm81.8 167.1c22.1-45.2 82.2-103.6 167.6-132.8 29.7 77.3 42 142.1 45.2 160.6-68.1 29-150 21.1-212.8-27.9zm248.4 8.5c-2.2-12.9-13.4-74.9-41.2-151 66.4-10.6 124.7 6.8 131.9 9.1-9.4 58.9-43.3 109.8-90.8 142z" />
                                </svg>
                            </span></button><button
                            class="bg-white text-gray-900 shadow-lg font-normal   items-center justify-center align-center rounded-full outline-none focus:outline-none mr-2 p-3"
                            type="button">
                            <span class="[&>svg]:h-5">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 496 512">
                                    <!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc. -->
                                    <path
                                        d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3 .3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5 .3-6.2 2.3zm44.2-1.7c-2.9 .7-4.9 2.6-4.6 4.9 .3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8zM97.2 352.9c-1.3 1-1 3.3 .7 5.2 1.6 1.6 3.9 2.3 5.2 1 1.3-1 1-3.3-.7-5.2-1.6-1.6-3.9-2.3-5.2-1zm-10.8-8.1c-.7 1.3 .3 2.9 2.3 3.9 1.6 1 3.6 .7 4.3-.7 .7-1.3-.3-2.9-2.3-3.9-2-.6-3.6-.3-4.3 .7zm32.4 35.6c-1.6 1.3-1 4.3 1.3 6.2 2.3 2.3 5.2 2.6 6.5 1 1.3-1.3 .7-4.3-1.3-6.2-2.2-2.3-5.2-2.6-6.5-1zm-11.4-14.7c-1.6 1-1.6 3.6 0 5.9 1.6 2.3 4.3 3.3 5.6 2.3 1.6-1.3 1.6-3.9 0-6.2-1.4-2.3-4-3.3-5.6-2z" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                <div class="w-full lg:w-6/12 px-4">
                    <div class="flex flex-wrap items-top mb-6">
                        <div class="w-full lg:w-4/12 px-4 ml-auto">
                            <span class="block uppercase text-gray-600 text-sm font-semibold mb-2">Useful Links</span>
                            <ul class="list-unstyled">
                                <li>
                                    <a class="text-gray-700 hover:text-gray-900 font-semibold block pb-2 text-sm"
                                        href="#">About Us</a>
                                </li>
                                <li>
                                    <a class="text-gray-700 hover:text-gray-900 font-semibold block pb-2 text-sm"
                                        href="#">Blog</a>
                                </li>
                                <li>
                                    <a class="text-gray-700 hover:text-gray-900 font-semibold block pb-2 text-sm"
                                        href="#">Products</a>
                                </li>
                            </ul>
                        </div>
                        <div class="w-full lg:w-4/12 px-4">
                            <span class="block uppercase text-gray-600 text-sm font-semibold mb-2">Other
                                Resources</span>
                            <ul class="list-unstyled">
                                <li>
                                    <a class="text-gray-700 hover:text-gray-900 font-semibold block pb-2 text-sm"
                                        href="#">Terms &amp; Conditions</a>
                                </li>
                                <li>
                                    <a class="text-gray-700 hover:text-gray-900 font-semibold block pb-2 text-sm"
                                        href="#">Privacy Policy</a>
                                </li>
                                <li>
                                    <a class="text-gray-700 hover:text-gray-900 font-semibold block pb-2 text-sm"
                                        href="#">Contact Us</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="my-6 border-gray-400" />
            <div class="flex flex-wrap items-center md:justify-between justify-center">
                <div class="w-full md:w-4/12 px-4 mx-auto text-center">
                    <div class="text-sm text-gray-600 font-semibold py-1">
                        Copyright © {{ currentYear }}
                        <a href="#" class="text-gray-600 hover:text-gray-900">COI</a>.
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>